import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import { Copy, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { SelectField, TextField } from '@/components/form-fields';
import { useDetailTitle } from '@/components/page-header';
import {
  EmptyState,
  ListRow,
  ListShell,
  ReadOnlyNotice,
  SectionCard,
} from '@/components/settings/settings-kit';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth';
import {
  deleteMediaAsset,
  getMediaAsset,
  getMediaUsage,
  MEDIA_FOLDERS,
  updateMediaAsset,
} from '@/lib/media';
import { formatBytes } from './media.index';

/**
 * One asset: what it is, what it is called, and what would break if it went.
 *
 * The delete is guarded on usage rather than on a confirmation alone. An
 * image referenced by a product or a post is deletable only after those
 * references are removed, because R2 has no undo and the alternative is a
 * storefront full of broken images that nobody can trace back to this click.
 */
export const Route = createFileRoute('/media/$assetId')({
  component: MediaDetailPage,
});

function MediaDetailPage() {
  const { assetId } = useParams({ from: '/media/$assetId' });
  const { activeAccountId, can } = useAuth();
  const accountId = activeAccountId ?? '';
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const canManage = can('cms.manage');

  const asset = useQuery({
    queryKey: ['media-asset', accountId, assetId],
    queryFn: () => getMediaAsset(accountId, assetId),
    enabled: Boolean(accountId),
  });

  const usage = useQuery({
    queryKey: ['media-usage', accountId, assetId],
    queryFn: () => getMediaUsage(accountId, asset.data as NonNullable<typeof asset.data>),
    enabled: Boolean(accountId && asset.data),
  });

  useDetailTitle(asset.data?.filename ?? `Asset ${assetId.slice(0, 8)}`);

  const [filename, setFilename] = useState('');
  const [alt, setAlt] = useState('');
  const [folder, setFolder] = useState('content');
  const [tags, setTags] = useState('');

  useEffect(() => {
    if (!asset.data) return;

    setFilename(asset.data.filename);
    setAlt(asset.data.alt ?? '');
    setFolder(asset.data.folder ?? 'content');
    setTags(asset.data.tags.join(', '));
  }, [asset.data]);

  const save = useMutation({
    mutationFn: () =>
      updateMediaAsset(accountId, assetId, {
        filename: filename.trim(),
        alt: alt.trim() || null,
        folder,
        tags: tags
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean),
      }),
    onSuccess: () => {
      toast.success('Saved.');
      queryClient.invalidateQueries({ queryKey: ['media-asset', accountId, assetId] });
      queryClient.invalidateQueries({ queryKey: ['media', accountId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: () => deleteMediaAsset(accountId, asset.data as NonNullable<typeof asset.data>),
    onSuccess: () => {
      toast.success('Deleted.');
      queryClient.invalidateQueries({ queryKey: ['media', accountId] });
      navigate({ to: '/media' });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (asset.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!asset.data) {
    return (
      <EmptyState>
        No such asset.{' '}
        <Link to="/media" className="underline">
          Back to the library
        </Link>
      </EmptyState>
    );
  }

  const references = (usage.data?.products.length ?? 0) + (usage.data?.posts.length ?? 0);

  return (
    <div className="space-y-6">
      {!canManage && <ReadOnlyNotice permission="cms.manage" />}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="bg-muted overflow-hidden rounded-lg border">
          {asset.data.publicUrl ? (
            <img
              src={asset.data.publicUrl}
              alt={asset.data.alt ?? asset.data.filename}
              className="max-h-[560px] w-full object-contain"
            />
          ) : null}
        </div>

        <div className="space-y-6">
          <SectionCard title="Details" description="Shown as alt text wherever this is rendered.">
            <fieldset disabled={!canManage} className="space-y-4">
              <TextField id="asset-filename" label="Name" value={filename} onChange={setFilename} />
              <TextField
                id="asset-alt"
                label="Alt text"
                description="What the image shows, for screen readers and search."
                value={alt}
                onChange={setAlt}
              />
              <SelectField
                id="asset-folder"
                label="Folder"
                value={folder}
                onChange={setFolder}
                options={MEDIA_FOLDERS.map((value) => ({ value, label: value }))}
              />
              <TextField
                id="asset-tags"
                label="Tags"
                description="Comma separated."
                value={tags}
                onChange={setTags}
              />

              <Button type="button" onClick={() => save.mutate()} disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
            </fieldset>
          </SectionCard>

          <SectionCard title="File">
            <dl className="space-y-2 text-sm">
              <Fact label="Type" value={asset.data.mimeType ?? '—'} />
              <Fact
                label="Dimensions"
                value={
                  asset.data.width && asset.data.height
                    ? `${asset.data.width} × ${asset.data.height}`
                    : '—'
                }
              />
              <Fact label="Size" value={formatBytes(asset.data.sizeBytes)} />
              <Fact
                label="Uploaded"
                value={new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(
                  new Date(asset.data.createdAt),
                )}
              />
            </dl>

            {asset.data.publicUrl && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(asset.data?.publicUrl ?? '');
                  toast.success('URL copied.');
                }}
              >
                <Copy className="size-4" />
                Copy URL
              </Button>
            )}
          </SectionCard>

          <SectionCard
            title="Used by"
            description="Found by matching the address this file is served at."
          >
            {usage.isLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : references ? (
              <ListShell>
                {usage.data?.products.map((product) => (
                  <ListRow
                    key={product.id}
                    title={
                      <Link
                        to="/products/$productId"
                        params={{ productId: product.id }}
                        className="hover:underline"
                      >
                        {product.name}
                      </Link>
                    }
                    subtitle="Product"
                  />
                ))}
                {usage.data?.posts.map((post) => (
                  <ListRow
                    key={post.id}
                    title={
                      <Link
                        to="/blog/$postId"
                        params={{ postId: post.id }}
                        className="hover:underline"
                      >
                        {post.title}
                      </Link>
                    }
                    subtitle="Post"
                  />
                ))}
              </ListShell>
            ) : (
              <EmptyState>Nothing points at this file.</EmptyState>
            )}

            {canManage && (
              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="self-start"
                  disabled={references > 0 || remove.isPending || usage.isLoading}
                  onClick={() => remove.mutate()}
                >
                  <Trash2 className="size-4" />
                  {remove.isPending ? 'Deleting…' : 'Delete file'}
                </Button>
                {references > 0 && (
                  <p className="text-muted-foreground text-xs">
                    Still used in {references} {references === 1 ? 'place' : 'places'}. Remove it
                    there first — R2 has no undo.
                  </p>
                )}
              </div>
            )}
          </SectionCard>
        </div>
      </div>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right">{value}</dd>
    </div>
  );
}
